import { Box, Paper } from '@mui/material';
import { getInvoiceEmailRecipients } from '@vy/services';
import { CURRENCIES } from '@vy/types';
import { isNotEmptyArray, isNotNullOrUndefined } from '@vy/utils';
import React, { useCallback, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useCustomer } from '../../../hooks/requests/use-customer';
import { useCreditNotes } from '../../../hooks/requests/use-credit-notes';
import { useConnectedIntegrations } from '../../../hooks/requests/use-integrations';
import { useValidatedParams } from '../../../hooks/use-validated-params';
import { CreditNotePdfPreviewDrawer } from './customer-credit-notes-page-components/CreditNotePdfPreviewDrawer';
import { CustomerCreditNotesTable } from './customer-credit-notes-page-components/CustomerCreditNotesTable';

export const CustomerCreditNotesPage: React.FC = () => {
  const { customerId } = useValidatedParams<['customerId']>('customerId');
  const { data, isFetching } = useCreditNotes(customerId);
  const { data: customerData } = useCustomer(customerId, { suspense: false });
  const { data: connectedIntegrationsData } = useConnectedIntegrations();
  const currency = customerData?.customer.settings?.currency ?? CURRENCIES.Usd;
  const customerName = customerData?.customer.name ?? '';
  const customerContacts = customerData?.customer.contacts;
  const contactEmails = isNotEmptyArray(customerContacts)
    ? getInvoiceEmailRecipients(customerContacts)
    : [];

  const [previewCreditLedgerId, setPreviewCreditLedgerId] = useState<string | null>(null);

  const creditNoteEntries = data?.creditNotes ?? [];
  const connectedIntegrations = connectedIntegrationsData ?? [];

  const handleViewPdf = useCallback((creditLedgerId: string) => {
    setPreviewCreditLedgerId(creditLedgerId);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewCreditLedgerId(null);
  }, []);

  return (
    <>
      <Helmet>
        <title>Customer Credit Notes</title>
      </Helmet>

      <Paper
        sx={{
          borderRadius: '10px',
          height: '63vh',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CustomerCreditNotesTable
            entries={creditNoteEntries}
            isLoading={isFetching}
            onViewPdf={handleViewPdf}
            customerId={customerId}
            currency={currency}
            connectedIntegrations={connectedIntegrations}
            customerName={customerName}
            contactEmails={contactEmails}
          />
        </Box>
      </Paper>

      <CreditNotePdfPreviewDrawer
        open={isNotNullOrUndefined(previewCreditLedgerId)}
        onClose={handleClosePreview}
        creditLedgerId={previewCreditLedgerId}
      />
    </>
  );
};
